import Button from "@metacrm/metacrm-material-ui/dist/Button";
import RangeSlider from "@metacrm/metacrm-material-ui/dist/RangeSlider";
import { Box, Grid } from "@mui/material";
import PureModal from "components/PureModal";
import React, { useEffect, useRef, useState } from "react";
import { StyledLabel, StyledLeftFieldContainer } from "./FilterModal.styles";
import { cloneDeep, isEqual, startCase } from "lodash-es";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import DatePicker from "@metacrm/metacrm-material-ui/dist/DatePicker";
import { apiUrl } from "features/configure";
import { useFetchTags, useFetchUserList } from "features/metadesk/redux/hooks";
import { useOutletContext } from "react-router";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import {
  selectEcosystemList,
  selectEnabledEcosystems,
  selectEcosystemsChainIds,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import SelectToken from "features/metadesk/components/SelectToken/SelectToken";
import InputField from "@metacrm/metacrm-material-ui/dist/InputField";
import _ from "lodash";
import { useMappingLabel } from "../useMappingLabel";
import { useIntl } from "react-intl";
import { getChainList } from "features/helpers/utils";

export default function FilterModal({ open, handleClose, ...props }) {
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const { userListFilter: filter, updateUserListFilter: setFilter } =
    useFetchUserList();
  const { sourceMapping, lastEngagementMapping } = useMappingLabel();

  const initFilterData = {
    ecosystems: [],
    chainIds: [],
    cexLabels: [],
    walletTotalValue: [0, 50000],
    walletAge: [0, 144],
    source: null,
    tags: [],
    lastEngagementType: null,
    lastEngagement: [null, null],
    assets: [
      {
        chainId: null,
        assetType: null,
        token: null,
      },
    ],
  };
  const datePickerRef = useRef(null);
  const firstTimeRef = useRef(true);
  const { userSource, fetchUserSource } = useFetchUserList();

  const [tempFilter, setTempFilter] = useState(cloneDeep(initFilterData));

  useEffect(() => {
    try {
      setTempFilter({
        ...initFilterData,
        ...filter,
        ecosystems:
          filter?.ecosystems
            ?.map((item) =>
              ecosystemList.find((chain) => chain.value === item.value)
            )
            .filter(Boolean) || [],
        chainIds:
          filter?.chainIds
            ?.map((item) =>
              chainList.find((chain) => chain.value === item.value)
            )
            .filter(Boolean) || [],
      });
    } catch (e) {
      console.log(e);
    }
  }, [filter]);

  function handleChange(key, newValue) {
    if (key === "ecosystems") {
      const prevEcosystems = tempFilter.ecosystems.map((eco) => eco.value);
      const nextEcosystems = newValue.map((eco) => eco.value);

      // find the removed ecosystems
      const removedEcosystems = _.difference(prevEcosystems, nextEcosystems);

      // according to the removed ecosystems, remove the corresponding chainIds from the selected chainIds
      const removedChainIds = _.flatMap(
        removedEcosystems,
        (eco) => ecosystemsChainIds[eco] || []
      );
      const updatedChainIds = tempFilter.chainIds.filter(
        (chain) => !removedChainIds.includes(chain.value)
      );

      setTempFilter({
        ...tempFilter,
        ecosystems: newValue,
        chainIds: updatedChainIds,
      });
    } else {
      setTempFilter({ ...tempFilter, [key]: newValue });
    }
  }

  function formatWalletAge(age) {
    const years = Math.floor(age / 12);
    const months = age % 12;
    return {
      years,
      months,
    };
  }

  function applyFilter() {
    const updatedFields = {};
    Object.keys(tempFilter).forEach((key) => {
      if (
        !isEqual(tempFilter[key], initFilterData[key]) ||
        (filter[key] &&
          filter[key]?.length > 0 &&
          !isEqual(tempFilter[key], filter[key]))
      ) {
        if (key === "walletTotalValue" || key === "walletAge") {
          // 如果 `walletTotalValue` 沒有改變，不丟進 result
          if (
            tempFilter[key][0] !== initFilterData[key][0] ||
            tempFilter[key][1] !== initFilterData[key][1]
          ) {
            updatedFields[key] = tempFilter[key];
          }
        }
        if (key === "assets") {
          const assets = tempFilter.assets.filter(
            (item) => item.chainId && item.assetType && item.token
          );
          if (assets?.length > 0) {
            updatedFields[key] = assets;
          }
        } else {
          updatedFields[key] = tempFilter[key];
        }
      }
    });
    // 更新過的字段才會被應用到 filter 中
    setFilter({ ...filter, ...updatedFields, page: 1 });
    handleClose();
    firstTimeRef.current = false;
  }

  function handleChangeAsset(index, key, value) {
    const tempAsset = cloneDeep(tempFilter.assets);
    tempAsset[index][key] = value;
    setTempFilter({ ...tempFilter, assets: tempAsset });
  }

  function handleAddAsset() {
    const tempAsset = cloneDeep(tempFilter.assets);
    tempAsset.push({
      chainId: null,
      assetType: null,
      token: null,
    });
    setTempFilter({ ...tempFilter, assets: tempAsset });
  }

  const [cexLabelOptions, setCexLabelOptions] = useState([]);

  useEffect(() => {
    async function fetchCexLabelOptions() {
      try {
        const { data } = await axios.get(
          `${apiUrl}/api/user/${entityName}/filters`
        );
        const { cexLabels } = data;
        setCexLabelOptions(
          (cexLabels || []).map((item) => ({
            id: uuidv4(),
            label: item,
            value: item,
          }))
        );
      } catch (error) {
        console.error("Failed to fetch cex label options:", error);
        setCexLabelOptions([]);
      }
    }
    fetchUserSource({ entityName });
    fetchCexLabelOptions();
  }, []);

  const chainList = getChainList();
  const ecosystemsChainIds = useSelector(selectEcosystemsChainIds);
  const enabledEcosystems = useSelector(selectEnabledEcosystems);
  const ecosystemList = useSelector(selectEcosystemList);

  const selectedEcosystems = tempFilter?.ecosystems?.map((eco) => eco.value);
  const filteredChainIds = _.isEmpty(selectedEcosystems)
    ? _.flatMap(Object.values(ecosystemsChainIds))
    : _.flatMap(selectedEcosystems, (eco) => ecosystemsChainIds[eco] || []);

  const chainIdOptions = filteredChainIds
    .map((item) => chainList.find((chain) => chain.value === item))
    .filter((item) => item);
  const ecosystemOptions = enabledEcosystems
    ?.map((item) => ecosystemList.find((eco) => eco.value === item))
    .filter((item) => item);

  const { tags } = useFetchTags();
  const tagOptions = (tags[entityName] || []).map((item) => ({
    ...item,
    label: item.value,
  }));

  const sourceOptions = userSource
    ? [
        {
          label: formatMessage({ id: "global.placeholder.select" }),
          value: "",
        },
        ..._.chain(userSource)
          ?.groupBy("indicator")
          ?.keys()
          ?.map((o) => {
            return {
              label: sourceMapping(o),
              value: o,
            };
          })
          .value(),
      ]
    : [];

  const lastEngagementOptions = [
    { label: formatMessage({ id: "global.placeholder.select" }), value: "" },
    ...Object.keys(lastEngagementMapping("all")).map((key) => ({
      label: lastEngagementMapping(key),
      value: key,
    })),
  ];

  const [openSelectToken, setOpenSelectToken] = useState("");

  function handleValueBlur(e) {
    const inputValue = Number(e.target.value.replace(/[^0-9]/g, "")); // 去除非數字的符號
    const max = 50000;

    let arr = [...tempFilter.walletTotalValue];
    if (inputValue > max) {
      arr[1] = max;
    }
    if (arr[0] > arr[1]) {
      [arr[0], arr[1]] = [arr[1], arr[0]];
    }
    handleChange("walletTotalValue", arr);
  }

  function handleAgeBlur(e, type) {
    const inputValue = Number(e.target.value.replace(/[^0-9]/g, "")); // 去除非數字的符號
    const max = type === "y" ? 12 : 11;

    let arr = [...tempFilter.walletAge];
    if (inputValue > max) {
      arr[1] = max;
    }
    if (arr[0] > arr[1]) {
      [arr[0], arr[1]] = [arr[1], arr[0]];
    }
    handleChange("walletAge", arr);
  }

  return (
    <PureModal
      open={open}
      closeBtn={false}
      onClose={handleClose}
      sx={{ zIndex: 10000 }}
      maxWidth="848px"
      {...props}
    >
      <Box
        p="24px 24px 24px 32px"
        borderBottom="1px solid #E6E6E6"
        fontSize={20}
        fontWeight={700}
        className="item-flex"
        justifyContent="space-between"
      >
        <Box>{formatMessage({ id: "popup.filters.title" })}</Box>
        <i
          className="meta-crm-icon-ic_cancel font-size-20"
          onClick={handleClose}
          style={{ cursor: "pointer" }}
        />
      </Box>
      <Box
        p="24px 32px"
        display="flex"
        maxHeight="calc(85vh - 141px)"
        overflow="auto"
      >
        <Box flex={1}>
          {/* Ecosystems */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.filters.ecosystems" })}
            </StyledLabel>
            <AutoCompleteDropdown
              value={tempFilter?.ecosystems}
              options={ecosystemOptions}
              select
              multiple
              onChange={(e, v) => handleChange("ecosystems", v)}
              placeholder={formatMessage({ id: "global.filter.select" })}
            />
          </StyledLeftFieldContainer>
          {/* Active Chains */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.filters.activeChains" })}
            </StyledLabel>
            <AutoCompleteDropdown
              value={tempFilter?.chainIds}
              options={chainIdOptions}
              select
              multiple
              onChange={(e, v) => handleChange("chainIds", v)}
              placeholder={formatMessage({ id: "global.filter.select" })}
            />
          </StyledLeftFieldContainer>
          {/* CEX Label */}
          <StyledLeftFieldContainer>
            <StyledLabel sx={{ marginTop: "16px" }}>
              {formatMessage({ id: "cdp.filters.cex" })}
            </StyledLabel>
            <AutoCompleteDropdown
              value={tempFilter.cexLabels}
              options={cexLabelOptions}
              select
              multiple
              onChange={(e, v) => handleChange("cexLabels", v)}
              placeholder={formatMessage({ id: "global.filter.select" })}
            />
          </StyledLeftFieldContainer>
          {/* Wallet Total Value */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.filters.walletTotalValue" })}
            </StyledLabel>
            <RangeSlider
              values={tempFilter.walletTotalValue}
              onChange={(e, v) => handleChange("walletTotalValue", v)}
              min={0}
              max={50000}
              step={1}
              minText={formatMessage({ id: "global.filter.min" })}
              maxText={formatMessage({ id: "global.filter.max" })}
            />
            <Box
              sx={{
                mt: "20px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <InputField
                value={`$${tempFilter.walletTotalValue[0]}`}
                onBlur={handleValueBlur}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  handleChange("walletTotalValue", [
                    Number(value),
                    tempFilter.walletTotalValue[1],
                  ]);
                }}
                textAlign="center"
                sx={{ width: "100px" }}
              />
              <InputField
                value={`$${tempFilter.walletTotalValue[1]}${
                  tempFilter.walletTotalValue[1] === 50000 ? "+" : ""
                }`}
                onBlur={handleValueBlur}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  handleChange("walletTotalValue", [
                    tempFilter.walletTotalValue[0],
                    Number(value),
                  ]);
                }}
                textAlign="center"
                sx={{ width: "100px" }}
              />
            </Box>
          </StyledLeftFieldContainer>
          {/* Wallet Age */}

          <StyledLeftFieldContainer>
            <StyledLabel sx={{ marginTop: "32px" }}>
              {formatMessage({ id: "cdp.filters.walletAge" })}
            </StyledLabel>
            <RangeSlider
              values={tempFilter.walletAge}
              onChange={(e, v) => handleChange("walletAge", v)}
              min={0}
              max={144}
              step={1}
              minText={formatMessage({ id: "global.filter.min" })}
              maxText={formatMessage({ id: "global.filter.max" })}
            />
            <Box
              sx={{
                mt: "20px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box className="item-flex" gap="4px">
                <InputField
                  value={`${formatWalletAge(tempFilter.walletAge[0]).years}y`}
                  onBlur={(e) => handleAgeBlur(e, "y")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    handleChange("walletAge", [
                      Number(value) * 12,
                      tempFilter.walletAge[1],
                    ]);
                  }}
                  textAlign="center"
                  sx={{ width: "58px" }}
                />
                <InputField
                  value={`${formatWalletAge(tempFilter.walletAge[0]).months}m`}
                  onBlur={(e) => handleAgeBlur(e, "m")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    handleChange("walletAge", [
                      Number(value),
                      tempFilter.walletAge[1],
                    ]);
                  }}
                  textAlign="center"
                  sx={{ width: "58px" }}
                />
              </Box>
              <Box className="item-flex" gap="4px">
                <InputField
                  value={`${formatWalletAge(tempFilter.walletAge[1]).years}y`}
                  onBlur={(e) => handleAgeBlur(e, "y")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    handleChange("walletAge", [
                      tempFilter.walletAge[0],
                      Number(value) * 12,
                    ]);
                  }}
                  textAlign="center"
                  sx={{ width: "58px" }}
                />
                <InputField
                  value={`${formatWalletAge(tempFilter.walletAge[1]).months}m`}
                  onBlur={(e) => handleAgeBlur(e, "m")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    handleChange("walletAge", [
                      tempFilter.walletAge[0],
                      Number(value),
                    ]);
                  }}
                  textAlign="center"
                  sx={{ width: "58px" }}
                />
              </Box>
            </Box>
          </StyledLeftFieldContainer>
        </Box>
        <Box width="1px" mx="32px" bgcolor="#E2E0E0"></Box>
        <Box flex={1}>
          {/* Source */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.filters.source" })}
            </StyledLabel>
            <AutoCompleteDropdown
              value={tempFilter.source}
              options={sourceOptions}
              onChange={(e, v) => handleChange("source", v)}
              placeholder={formatMessage({ id: "global.filter.select" })}
            />
          </StyledLeftFieldContainer>
          {/* Tags */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.filters.tags" })}
            </StyledLabel>
            <AutoCompleteDropdown
              value={tempFilter.tags}
              options={tagOptions}
              isTag
              multiple
              onChange={(e, v) => handleChange("tags", v)}
              placeholder={formatMessage({ id: "global.filter.select" })}
            />
          </StyledLeftFieldContainer>
          {/* Last Engagement Type */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.filters.lastEngagementType" })}
            </StyledLabel>
            <AutoCompleteDropdown
              value={tempFilter.lastEngagementType}
              options={lastEngagementOptions}
              select
              onChange={(e, v) => handleChange("lastEngagementType", v)}
              placeholder={formatMessage({ id: "global.filter.select" })}
            />
          </StyledLeftFieldContainer>
          {/* Last Engagement Time Range */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.filters.lastEngagementTimeRange" })}
            </StyledLabel>
            <DatePicker
              allText={formatMessage({ id: "global.filter.all" })}
              ref={datePickerRef}
              dateRange={tempFilter.lastEngagement}
              setDateRange={(value) => handleChange("lastEngagement", value)}
              inputContainerStyle={{ height: "40px" }}
              quickSelectOptions={[
                {
                  days: 1,
                  label: formatMessage({ id: "datePicker.range.day" }),
                },
                {
                  days: 7,
                  label: formatMessage({ id: "datePicker.range.week" }),
                },
                {
                  days: 30,
                  label: formatMessage({ id: "datePicker.range.month" }),
                },
                {
                  days: "all",
                  label: formatMessage({ id: "datePicker.range.all" }),
                },
                {
                  days: "custom",
                  label: formatMessage({ id: "datePicker.range.custom" }),
                },
              ]}
            />
          </StyledLeftFieldContainer>
          {/* Current Holdings */}
          <StyledLeftFieldContainer>
            <StyledLabel>
              {formatMessage({ id: "cdp.headline.holdings" })}
            </StyledLabel>
            {tempFilter.assets.map((item, i) => (
              <Box mt="8px">
                <Grid container spacing={1}>
                  <Grid item sm={6}>
                    <AutoCompleteDropdown
                      value={item.chainId}
                      options={chainList}
                      select
                      onChange={(e, v) =>
                        handleChangeAsset(i, "chainId", {
                          ...item.chainId,
                          value: v.value,
                          label: v.name,
                        })
                      }
                      placeholder={formatMessage({
                        id: "global.filter.select",
                      })}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    <AutoCompleteDropdown
                      value={item.assetType}
                      options={[
                        { label: "Token", value: "token" },
                        { label: "NFT", value: "nft" },
                      ]}
                      select
                      onChange={(e, v) => handleChangeAsset(i, "assetType", v)}
                      placeholder={formatMessage({
                        id: "global.filter.select",
                      })}
                    />
                  </Grid>
                  <Grid item sm={12}>
                    <AutoCompleteDropdown
                      value={item.token}
                      options={[{ label: "", value: "" }]}
                      select
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenSelectToken({
                          index: i,
                          chainId: item.chainId?.value,
                          assetType: item.assetType?.value,
                        });
                      }}
                      placeholder={formatMessage({
                        id: "global.filter.select",
                      })}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Box
              fontSize={12}
              fontWeight={700}
              color="#7B61FF"
              p="4px"
              mt="8px"
              className="item-flex"
              sx={{ cursor: "pointer" }}
              onClick={handleAddAsset}
            >
              <i className="meta-crm-icon-ic_add font-size-16" />
              <Box ml="4px">{formatMessage({ id: "global.action.add" })}</Box>
            </Box>
          </StyledLeftFieldContainer>
        </Box>
      </Box>
      <Box
        p="16px 32px"
        boxShadow="0px 4px 20px 0px rgba(0, 0, 0, 0.10)"
        className="item-flex"
        justifyContent="space-between"
      >
        <Box
          sx={{
            color: "#7B61FF",
            textDecoration: "underline",
            cursor: "pointer",
          }}
          onClick={() => setTempFilter(cloneDeep(initFilterData))}
        >
          {formatMessage({ id: "global.btn.clearFilter" })}
        </Box>

        <Box className="item-flex" gap="12px">
          <Button onClick={handleClose} variant="outlined">
            {formatMessage({ id: "global.btn.cancel" })}
          </Button>
          <Button
            onClick={applyFilter}
            disabled={
              isEqual(tempFilter, initFilterData) && firstTimeRef.current
            }
          >
            {formatMessage({ id: "global.btn.applyFilter" })}
          </Button>
        </Box>
      </Box>
      {openSelectToken && (
        <SelectToken
          open={openSelectToken}
          entityName={entityName}
          title="Current Holding Menu"
          handleClose={() => setOpenSelectToken("")}
          value={tempFilter.assets[openSelectToken.index].token}
          setValue={(value) => {
            handleChangeAsset(openSelectToken.index, "token", {
              label: value.name,
              value: value.value,
            });
            setOpenSelectToken("");
          }}
          chainId={openSelectToken?.chainId}
          assetType={openSelectToken?.assetType}
        />
      )}
    </PureModal>
  );
}
